import { cn, normalizeLanguageCode } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/shared/components';
import { FC, FormEvent, useCallback, useState } from 'react';
import { Modal } from '@/shared/components/Modal';
import { Button, Checkbox, Input } from '@/shared/shadcn';
import { GlobalUserAuthProvider } from '@/app/store/api/restApi';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';
import { useOAuthLogin } from '@/features/auth/hooks/useOAuthLogin';
import { VkIcon, YandexIcon } from '@/assets/icons';
import { useHandleDocumentNavigate } from '@/features/documents/hooks/useHandleDocumentNavigate';
import { useRequestMagicAuthMutation } from '@/entities/auth/api';
import { getMagicAuthDomainError, isValidMagicEmail, saveMagicAuthRequest } from '../lib';
import { PATH } from '@/app/routes';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type Provider = {
  id: GlobalUserAuthProvider;
  labelKey: 'login.providers.yandex' | 'login.providers.vk';
  Icon: FC<{ width?: number; height?: number }>;
};

const PROVIDERS: ReadonlyArray<Provider> = [
  { id: GlobalUserAuthProvider.Vk, labelKey: 'login.providers.vk', Icon: VkIcon },
  { id: GlobalUserAuthProvider.Yandex, labelKey: 'login.providers.yandex', Icon: YandexIcon },
];

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AuthModal: FC<AuthModalProps> = ({ open, onOpenChange }) => {
  const { t, i18n } = useTranslation('auth');
  const { t: tDocs } = useTranslation('documents');
  const navigate = useNavigate();
  const { handleNavigate } = useHandleDocumentNavigate();
  const { legalDocuments } = useAppSelector((state) => state.documents);
  const { agreementsAccepted, demoMode } = useAppSelector((state) => state.application);
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const handleSuccess = useCallback(() => onOpenChange(false), [onOpenChange]);
  const { pending, errorMessage, startLogin, setPending } = useOAuthLogin({ onSuccess: handleSuccess });
  const [requestMagicAuth, { isLoading: isRequestingMagicLink }] = useRequestMagicAuthMutation();

  const [email, setEmail] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const isConsentSatisfied = agreementsAccepted || consentChecked || !legalDocuments;
  const normalizedEmail = email.trim();
  const emailValid = isValidMagicEmail(normalizedEmail);
  const authPending = pending !== null || isRequestingMagicLink;

  // Authorization is unavailable when the chain has no legal data — the window must never open.
  if (!open || !demoMode.hasLegalInfo) return null;

  const handleProviderClick = (id: GlobalUserAuthProvider) => {
    if (!isConsentSatisfied) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    startLogin(id);
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isConsentSatisfied) {
      setConsentError(true);
      return;
    }
    if (!emailValid) {
      toast.error(t('magic.invalidEmail'));
      return;
    }

    setConsentError(false);
    try {
      const result = await requestMagicAuth({
        magicAuthRequestDto: {
          email: normalizedEmail,
          languageCode: normalizeLanguageCode(i18n.language),
        },
      }).unwrap();
      saveMagicAuthRequest({ email: normalizedEmail, ...result.data });
      onOpenChange(false);
      navigate(PATH.magicLinkVerify);
    } catch (error) {
      const domainError = getMagicAuthDomainError(error);
      toast.error(t(domainError.code === 'MAGIC_AUTH_RATE_LIMITED' ? 'magic.rateLimited' : 'magic.requestError'));
    }
  };

  return (
    <Modal
      onClose={() => {
        onOpenChange(false);
        setPending(null);
        setConsentError(false);
      }}
      isDialog
      className='bg-black/25'
      contentClassName={cn('max-w-[400px] p-6 rounded-xl bg-background', isMinimalistic && 'rounded-none')}
    >
      <form onSubmit={handleEmailSubmit} className='flex flex-col gap-4'>
        <div className='flex flex-col gap-1.5'>
          <Typography type='span' color='cardForeground' className='text-2xl leading-none font-semibold tracking-tight'>
            {t('login.title')}
          </Typography>
          <Typography variant='subtitle1' color='mutedForeground'>
            {t('login.subtitle')}
          </Typography>
        </div>

        <div className='flex flex-col gap-3'>
          {PROVIDERS.map(({ id, labelKey, Icon }) => {
            return (
              <Button
                key={id}
                type='button'
                variant='outline'
                disabled={authPending}
                onClick={() => handleProviderClick(id)}
                className='h-10 w-full gap-0 justify-start p-2 text-accent-foreground [&_svg]:size-6'
              >
                <Icon width={24} height={24} />
                <span className='flex-1 min-w-0 text-center'>{t(labelKey)}</span>
              </Button>
            );
          })}
        </div>

        <Typography variant='subtitle2' color='mutedForeground' className='text-center'>
          {t('magic.or')}
        </Typography>

        <Input
          type='email'
          autoComplete='email'
          maxLength={320}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t('magic.emailPlaceholder')}
          disabled={authPending}
        />

        <Button
          type='submit'
          className='w-full'
          isLoading={isRequestingMagicLink}
          disabled={!emailValid || !isConsentSatisfied || authPending}
        >
          {t('magic.continueWithEmail')}
        </Button>

        {legalDocuments && (
          <div className='flex gap-2'>
            {!agreementsAccepted && (
              <Checkbox
                checked={consentChecked}
                onCheckedChange={(checked) => {
                  setConsentChecked(checked === true);
                  if (checked) setConsentError(false);
                }}
                className={cn('mt-0.5', consentError && 'border-destructive border-2')}
              />
            )}
            <Typography variant='subtitle3'>
              {tDocs('agreements.agreeTo')}{' '}
              <Typography
                type='span'
                color='primary'
                variant='subtitle3'
                onClick={() => {
                  handleNavigate({
                    fileName: tDocs('sections.consentPD'),
                    id: legalDocuments.consentToPersonalDataProcessing?.id ?? '',
                    url: legalDocuments.consentToPersonalDataProcessing?.url ?? '',
                    createdAt: legalDocuments.consentToPersonalDataProcessing?.createdAt ?? '',
                  });
                  onOpenChange(false);
                }}
                className='cursor-pointer'
              >
                {tDocs('agreements.processingAgree')}
              </Typography>{' '}
              {tDocs('agreements.andWith')}{' '}
              <Typography
                type='span'
                color='primary'
                variant='subtitle3'
                onClick={() => {
                  handleNavigate({
                    fileName: tDocs('sections.termsOfUse'),
                    id: legalDocuments.termsOfUse?.id ?? '',
                    url: legalDocuments.termsOfUse?.url ?? '',
                    createdAt: legalDocuments.termsOfUse?.createdAt ?? '',
                  });
                  onOpenChange(false);
                }}
                className='cursor-pointer'
              >
                {tDocs('agreements.termsAgree')}
              </Typography>
            </Typography>
          </div>
        )}

        {errorMessage && (
          <Typography variant='text4' color='destructive'>
            {errorMessage}
          </Typography>
        )}
      </form>
    </Modal>
  );
};
